import type { FC, ReactNode } from 'react';
import { useCallback, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { formatCurrencyValue } from '@repo/common';
import Image from '../../../../shared/components/Image';
import TransWithFallback from '../../../../shared/components/Translations/TransWrapper';
import { useTranslator } from '../../../../shared/hooks/useTranslations';
import { testIds } from '../../../../core/testIds';
import { JACKPOT_WIN_MINOR_UNITS } from '../../constants';
import { FIREWORKS_ASSET } from '../../statisticsAssets';
import type { IJackpotWinRow } from '../../types/jackpotStatistics.types';
import { formatWinDate, formatWinTime } from '../../utils/jackpotStatistics';
import JackpotLevelLabel from './JackpotLevelLabel';
import styles from './JackpotStatistics.module.css';

interface JackpotStatisticsContentProps {
  jackpotName: string;
  wins: IJackpotWinRow[];
  /** Disabled while a game launch is already in flight (mirrors the game card's re-entry guard). */
  isLaunchBlocked: boolean;
  onSelectGame: (win: IJackpotWinRow) => void;
  header?: ReactNode;
  /**
   * Handed down so the drawer can reset the list to the top via `scrollTop`/`scrollTo` when the
   * ordering changes. `scrollIntoView` is banned here: inside the game-client iframe it scrolls
   * the HOST page on iOS Safari.
   */
  scrollerRef: React.Ref<HTMLDivElement>;
}

/**
 * Middle panel: the scrollable list of individual wins.
 *
 * Column headers are static labels, NOT sort controls — ordering is chosen in the footer.
 * Player ids arrive ALREADY MASKED from BE (`06*65`) and are rendered verbatim: the raw id never
 * reaches the lobby, and masking the masked value again would only corrupt it.
 */
const JackpotStatisticsContent: FC<JackpotStatisticsContentProps> = ({
  jackpotName,
  wins,
  isLaunchBlocked,
  onSelectGame,
  header,
  scrollerRef,
}) => {
  const translate = useTranslator();
  const isEmpty = wins.length === 0;

  // The column headings cast their shadow only while a row is actually sliding underneath them
  // (elevation on scroll). At rest they sit flat on the drawer's gradient — a permanent shadow
  // there reads as a dark bar, since the fill right below is the gradient's darkest stretch.
  // Ref-gated so the scroll handler re-renders only when the state flips, never per event.
  const tableHeadRef = useRef<HTMLDivElement | null>(null);
  const isElevatedRef = useRef(false);
  const [isElevated, setIsElevated] = useState(false);
  const handleScroll = useCallback(() => {
    const head = tableHeadRef.current;
    const firstRow = head?.nextElementSibling;
    if (!head || !firstRow) return;

    const elevated = firstRow.getBoundingClientRect().top < head.getBoundingClientRect().bottom - 0.5;
    if (elevated !== isElevatedRef.current) {
      isElevatedRef.current = elevated;
      setIsElevated(elevated);
    }
  }, []);

  return (
    <div className={styles.contentPanel}>
      {/* Focusable (out of the Tab order) so the drawer can hand keyboard scrolling to the list. */}
      <div
        className={styles.scroller}
        ref={scrollerRef}
        tabIndex={-1}
        onScroll={handleScroll}
        data-empty={isEmpty}
      >
        {header}
        <div className={styles.tableHead} ref={tableHeadRef} data-elevated={isElevated}>
          <span className={clsx(styles.headCell, styles.headCellCenter)}>{'#'}</span>
          <span className={styles.headCell}>{translate('statistics.player', 'Player')}</span>
          <span className={styles.headCell}>{translate('statistics.payout', 'Payout')}</span>
          <span className={styles.headCell}>{translate('statistics.time_and_date', 'Time & Date')}</span>
          <span className={styles.headCell}>{translate('statistics.game', 'Game')}</span>
        </div>

        {isEmpty && (
          <div className={styles.emptyState} data-testid={testIds.jackpots.statisticsEmptyState}>
            <Image src={FIREWORKS_ASSET} manualAffix=".svg" alt="" className={styles.emptyIcon} />
            <p className={styles.emptyText}>
              <TransWithFallback
                i18nKey="statistics.empty"
                customFallbackTranslation="The jackpot winner is waiting to be discovered. Be the first!"
              />
            </p>
          </div>
        )}

        {wins.map((win, index) => (
          <div key={win.id} className={styles.row} data-testid={testIds.jackpots.statisticsRow(index + 1)}>
            <span className={styles.rank}>{index + 1}</span>
            <span className={styles.player}>{win.playerId}</span>
            <span className={styles.payout}>
              <span className={styles.payoutLevel}>
                <JackpotLevelLabel jackpotName={jackpotName} levelName={win.levelName} withVipBadge />
              </span>
              <span className={styles.payoutAmount}>
                {formatCurrencyValue(win.amount, JACKPOT_WIN_MINOR_UNITS)}
                <span className={styles.payoutCurrency}>{win.currency}</span>
              </span>
            </span>
            <span className={styles.timeAndDate}>
              <span className={styles.time}>{formatWinTime(win.wonAt)}</span>
              <span className={styles.date}>{formatWinDate(win.wonAt)}</span>
            </span>
            <button
              type="button"
              className={styles.gameButton}
              disabled={isLaunchBlocked}
              aria-label={win.gameName}
              data-testid={testIds.jackpots.statisticsGameButton(win.gameKey)}
              onClick={() => onSelectGame(win)}
            >
              <Image src={`/verticals/${win.gameKey}`} alt={win.gameName} className={styles.gameThumb} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JackpotStatisticsContent;
